// FeaturePanel.js
import React, { useState, useEffect } from "react";
import IsVerificationFeatureRequired from "./IsVerificationFeatureRequired";
import { useMutation, useQuery } from "@apollo/client";
import { ADD_FEATURE, GET_VERIFICATION_FEATURE } from "../Pages/RoleFormPage";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
  FormControlLabel,
  Switch,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  MenuItem,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";

// Theme Preview Component
const ThemePreview = ({ theme }) => {
  const previewTheme = createTheme({
    palette: {
      primary: {
        main: theme.primary,
      },
      secondary: {
        main: theme.secondary,
      },
      error: {
        main: theme.error,
      },
      warning: {
        main: theme.warning,
      },
      info: {
        main: theme.info,
      },
      success: {
        main: theme.success,
      },
      background: {
        default: theme.background,
        paper: theme.surface,
      },
      text: {
        primary: theme.text,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            backgroundColor: theme.button,
            color: theme.text,
            "&:hover": {
              backgroundColor: theme.button,
              opacity: 0.8,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiInputAdornment-root .MuiSvgIcon-root": {
              color: theme.icon,
            },
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={previewTheme}>
      <Paper
        sx={{
          p: 3,
          backgroundColor: theme.background,
          border: `2px solid ${theme.primary}`,
          borderRadius: 2,
          minHeight: 400,
        }}
      >
        <Typography
          variant="h5"
          gutterBottom
          sx={{ color: theme.text, textAlign: "center", mb: 3 }}
        >
          Registration Form Preview
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Full Name"
              placeholder="Enter your full name"
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.surface,
                  "& fieldset": {
                    borderColor: theme.primary,
                  },
                  "&:hover fieldset": {
                    borderColor: theme.secondary,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: theme.text,
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              placeholder="Enter your email"
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.surface,
                  "& fieldset": {
                    borderColor: theme.primary,
                  },
                  "&:hover fieldset": {
                    borderColor: theme.secondary,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: theme.text,
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Country"
              defaultValue="US"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.surface,
                  "& fieldset": {
                    borderColor: theme.primary,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: theme.text,
                },
              }}
            >
              <MenuItem value="US">United States</MenuItem>
              <MenuItem value="UK">United Kingdom</MenuItem>
              <MenuItem value="CA">Canada</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ color: theme.text }}>
                Registration Type
              </FormLabel>
              <RadioGroup row defaultValue="attendee">
                <FormControlLabel
                  value="attendee"
                  control={<Radio sx={{ color: theme.primary }} />}
                  label="Attendee"
                  sx={{ color: theme.text }}
                />
                <FormControlLabel
                  value="speaker"
                  control={<Radio sx={{ color: theme.primary }} />}
                  label="Speaker"
                  sx={{ color: theme.text }}
                />
                <FormControlLabel
                  value="exhibitor"
                  control={<Radio sx={{ color: theme.primary }} />}
                  label="Exhibitor"
                  sx={{ color: theme.text }}
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox defaultChecked sx={{ color: theme.primary }} />
              }
              label="I agree to the terms and conditions"
              sx={{ color: theme.text }}
            />
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: theme.button,
                  color: theme.text,
                  "&:hover": {
                    backgroundColor: theme.button,
                    opacity: 0.8,
                  },
                }}
              >
                Register Now
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  borderColor: theme.primary,
                  color: theme.primary,
                  "&:hover": {
                    borderColor: theme.secondary,
                    backgroundColor: theme.surface,
                  },
                }}
              >
                Preview
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {/* Color indicators */}
        <Box mt={3}>
          <Divider sx={{ my: 2, borderColor: theme.primary }} />
          <Typography variant="body2" sx={{ color: theme.text, mb: 1 }}>
            Theme Colors:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {Object.entries(theme).map(([key, color]) => (
              <Chip
                key={key}
                label={key}
                size="small"
                sx={{
                  backgroundColor: color,
                  color:
                    key === "background" || key === "surface"
                      ? theme.text
                      : "#fff",
                  border: `1px solid ${theme.primary}`,
                }}
              />
            ))}
          </Stack>
        </Box>
      </Paper>
    </ThemeProvider>
  );
};

export default function FeaturePanel({ eventId, role }) {
  const [tags, setTags] = React.useState([]);
  const [savedFeature, setsavedFeature] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [theme, setTheme] = useState({
    primary: "#000000",
    secondary: "#0066cc",
    icon: "#000000",
    button: "#ffd700",
    text: "#000000",
    background: "#ffffff",
    surface: "#f5f5f5",
    error: "#dc3545",
    warning: "#ffc107",
    info: "#17a2b8",
    success: "#28a745",
  });
  const [expandedSections, setExpandedSections] = useState({});

  console.log(eventId, role);
  const { data, loading, error } = useQuery(GET_VERIFICATION_FEATURE, {
    variables: { eventId, role },
  });

  // Comprehensive features list with categories
  const featureCategories = {
    "Core Features": [
      "english",
      "arabic",
      "english-arabic",
      "iconEnable",
      "voice-enable",
    ],
    "Social Registration": [
      "register-facebook",
      "register-linkedin",
      "register-google",
    ],
    Verification: [
      "emailVerification",
      "OTP",
      "MemberCheck",
      "Captcha",
      "ConferenceChoices",
    ],
    Payment: ["PaymentEnable", "onlyPMT", "PromoCode", "member-discount"],
    "Form Features": [
      "ShareButton",
      "DownloadBadge",
      "GroupBookingEnable",
      "Enable Another Registration Button",
      "duplicateEmailAllowed",
      "ShowPreview",
      "WhatsappRegistration",
    ],
    "Location & Limits": ["GeoLocation", "DuplicateNotAllowed", "allowed:50"],
  };

  const allFeatures = Object.values(featureCategories).flat();

  const [addFeature] = useMutation(ADD_FEATURE);

  // The exact features string this screen loaded. The server needs it to tell a token the
  // operator deliberately removed here from one another app wrote into the same cell while
  // this screen was open — without it, removals were silently written back. See
  // api-admin resolvers/form.js `knownValue`.
  const [knownFeatures, setKnownFeatures] = useState(null);

  const handleSaveFeature = async () => {
    const field = "features";
    const value = JSON.stringify(tags);
    await addFeature({
      variables: { eventId, role, field, value, knownValue: knownFeatures ?? "[]" },
      refetchQueries: [
        { query: GET_VERIFICATION_FEATURE, variables: { eventId, role } },
      ],
      awaitRefetchQueries: true,
    });
    // What we just saved is now what the screen has seen.
    setKnownFeatures(value);
    setsavedFeature(tags);
  };

  const handleSaveTheme = () => {
    const field = "theme";
    addFeature({
      variables: { eventId, role, field, value: JSON.stringify(theme) },
    });
  };

  const handleTagsChange = (event, newValue) => {
    setTags(newValue);
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  useEffect(() => {
    if (data && data?.mailVerificationFeature) {
      let parsedData = [];
      let parsedTheme = {};
      const rawFeatures = data.mailVerificationFeature.features || "[]";
      // Tolerant on purpose: some events store features as a bare comma list rather than a JSON
      // array. JSON.parse threw on those, tags stayed empty, and the next Save wrote [] over a
      // live configuration. Read both shapes; we always write back JSON.
      parsedData = (() => {
        const text = String(rawFeatures).trim();
        if (!text) return [];
        if (text.startsWith("[")) {
          try {
            const arr = JSON.parse(text);
            return Array.isArray(arr) ? arr.map(String) : [];
          } catch (err) {
            console.error("Error parsing features:", err);
            return [];
          }
        }
        return text.split(",").map((t) => t.trim()).filter(Boolean);
      })();
      try {
        parsedTheme = JSON.parse(data.mailVerificationFeature.theme || "{}");
      } catch (err) {
        console.error("Error parsing the data:", err);
      }
      setKnownFeatures(rawFeatures);
      if (Array.isArray(parsedData)) {
        setTags(parsedData);
        setsavedFeature(parsedData);
      }
      if (Object.keys(parsedTheme).length > 0) {
        setTheme((prevTheme) => ({ ...prevTheme, ...parsedTheme }));
      }
    }
  }, [data]);

  if (loading) {
    return <LinearProgress />;
  }

  if (error) {
    return <Typography color="error">{error.message}</Typography>;
  }

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h5">Feature Panel</Typography>
        <Button
          variant="contained"
          startIcon={<SettingsIcon />}
          onClick={() => setOpenModal(true)}
          sx={{ borderRadius: 2 }}
        >
          Advanced Settings
        </Button>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }} variant="outlined">
            <Stack spacing={2}>
              <Autocomplete
                multiple
                freeSolo
                value={tags}
                options={allFeatures}
                onChange={handleTagsChange}
                renderTags={(value, getTagProps) =>
                  Array.isArray(value)
                    ? value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          color="primary"
                          label={option}
                          {...getTagProps({ index })}
                          key={index}
                        />
                      ))
                    : null
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Add features to this form"
                    variant="outlined"
                    fullWidth
                    placeholder="Type or select features..."
                  />
                )}
              />
              <Button
                onClick={handleSaveFeature}
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                sx={{ alignSelf: "flex-start" }}
              >
                Save Features
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Advanced Settings Modal */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, height: "90vh" },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h5" component="span">
              Advanced Form Settings
            </Typography>
            <IconButton onClick={() => setOpenModal(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ height: "calc(90vh - 140px)", overflow: "auto" }}
        >
          <Grid container spacing={3}>
            {/* Features by Category */}
            <Grid item xs={12} lg={4}>
              <Card variant="outlined" sx={{ height: "fit-content" }}>
                <CardHeader
                  title="Feature Categories"
                  avatar={<SecurityIcon color="primary" />}
                />
                <CardContent>
                  {Object.entries(featureCategories).map(
                    ([category, features]) => (
                      <Box key={category} mb={2}>
                        <Button
                          fullWidth
                          variant="text"
                          onClick={() => toggleSection(category)}
                          endIcon={
                            expandedSections[category] ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )
                          }
                          sx={{ justifyContent: "space-between", mb: 1 }}
                        >
                          {category}
                        </Button>
                        <Collapse in={expandedSections[category]}>
                          <Box pl={2}>
                            {features.map((feature) => (
                              <FormControlLabel
                                key={feature}
                                control={
                                  <Switch
                                    checked={tags.includes(feature)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setTags((prev) => [...prev, feature]);
                                      } else {
                                        setTags((prev) =>
                                          prev.filter((t) => t !== feature)
                                        );
                                      }
                                    }}
                                    size="small"
                                  />
                                }
                                label={
                                  <Typography variant="body2">
                                    {feature}
                                  </Typography>
                                }
                                sx={{ display: "block", mb: 0.5 }}
                              />
                            ))}
                          </Box>
                        </Collapse>
                        <Divider />
                      </Box>
                    )
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Theme Configuration */}
            <Grid item xs={12} lg={4}>
              <Card variant="outlined" sx={{ height: "fit-content" }}>
                <CardHeader
                  title="Theme Configuration"
                  avatar={<PaletteIcon color="primary" />}
                />
                <CardContent>
                  <Grid container spacing={2}>
                    {Object.entries(theme).map(([key, value]) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <TextField
                          fullWidth
                          label={key.charAt(0).toUpperCase() + key.slice(1)}
                          type="color"
                          value={value}
                          onChange={(e) =>
                            setTheme((prev) => ({
                              ...prev,
                              [key]: e.target.value,
                            }))
                          }
                          size="small"
                          InputProps={{
                            sx: { height: 40 },
                          }}
                        />
                      </Grid>
                    ))}
                    <Grid item xs={12}>
                      <Button
                        onClick={handleSaveTheme}
                        variant="contained"
                        startIcon={<SaveIcon />}
                        fullWidth
                      >
                        Save Theme
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Selected Features Preview */}
              <Card variant="outlined" sx={{ mt: 2 }}>
                <CardHeader title="Selected Features" />
                <CardContent>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        color="primary"
                        variant="outlined"
                        onDelete={() =>
                          setTags((prev) => prev.filter((_, i) => i !== index))
                        }
                        size="small"
                      />
                    ))}
                    {tags.length === 0 && (
                      <Typography color="text.secondary">
                        No features selected
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Live Theme Preview */}
            <Grid item xs={12} lg={4}>
              <Card variant="outlined">
                <CardHeader
                  title="Live Theme Preview"
                  avatar={<VisibilityIcon color="primary" />}
                  subheader="See how your form will look with the selected theme"
                />
                <CardContent sx={{ p: 0 }}>
                  <ThemePreview theme={theme} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button
              onClick={async () => {
              await handleSaveFeature();
              handleSaveTheme();
              setOpenModal(false);
            }}
            variant="contained"
            startIcon={<SaveIcon />}
          >
            Save All Settings
          </Button>
        </DialogActions>
      </Dialog>

      <IsVerificationFeatureRequired
        eventId={eventId}
        role={role}
        selectedFeatures={savedFeature}
      />
    </Box>
  );
}
